import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail, Pencil, Eye, RotateCcw, Loader2, Save, FileText, Lock, CheckCircle2, Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // template object
  const [previewing, setPreviewing] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch {
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleResetOverride = async (t) => {
    if (!window.confirm(
      `Revert "${t.label}" to its default? This will delete your custom version and future sends will use the original template.`
    )) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${t.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`"${t.label}" reverted to default`);
      fetchTemplates();
    } catch {
      toast.error('Failed to revert template');
    }
  };

  const editableTemplates = templates.filter(t => t.editable);
  const readOnlyTemplates = templates.filter(t => !t.editable);

  return (
    <div className="space-y-6">
      {/* Editable Templates */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-forest flex items-center gap-2">
            <Pencil className="h-5 w-5" />
            Editable Templates
            <Badge variant="outline">{editableTemplates.length}</Badge>
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            These templates can be customized. Saved overrides take effect immediately on the next email send.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-gray-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {editableTemplates.map((t) => (
                <div key={t.id} className="px-5 py-4 flex items-start justify-between gap-4 hover:bg-gray-50/60">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-gray-400 shrink-0" />
                      <span className="font-medium text-gray-900">{t.label}</span>
                      {t.has_override ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
                          Customized
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-gray-500">
                          Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{t.description}</p>
                    {t.has_override && t.override?.updated_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        Last edited {new Date(t.override.updated_at).toLocaleString()}
                        {t.override.updated_by ? ` by ${t.override.updated_by}` : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => setPreviewing(t)} data-testid={`tmpl-preview-${t.id}`}>
                      <Eye className="h-4 w-4 mr-1" /> Preview
                    </Button>
                    <Button size="sm" onClick={() => setEditing(t)} data-testid={`tmpl-edit-${t.id}`}>
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    {t.has_override && (
                      <Button
                        variant="outline" size="sm"
                        onClick={() => handleResetOverride(t)}
                        data-testid={`tmpl-reset-${t.id}`}
                        title="Revert to default"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Read-only Templates */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
            <Lock className="h-4 w-4" />
            View-Only Templates
            <Badge variant="outline">{readOnlyTemplates.length}</Badge>
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Locked to preserve their layouts. Reach out if you need any of these unlocked for editing.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {readOnlyTemplates.map((t) => (
              <div key={t.id} className="px-5 py-3 flex items-center justify-between gap-4 hover:bg-gray-50/60">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                    <span className="text-gray-900">{t.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPreviewing(t)} data-testid={`tmpl-preview-${t.id}`}>
                  <Eye className="h-4 w-4 mr-1" /> Preview
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <TemplateEditDialog
        template={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchTemplates(); }}
      />

      {/* Preview Dialog (defaults) */}
      <TemplatePreviewDialog
        template={previewing}
        onClose={() => setPreviewing(null)}
      />
    </div>
  );
};

// ----- Edit Dialog ---------------------------------------------------------

const TemplateEditDialog = ({ template, onClose, onSaved }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    if (!template) return;
    // Initialize from saved override if present, otherwise default
    setSubject(template.override?.subject || template.default_subject || '');
    setBody(template.override?.body || template.default_body || '');
    setPreviewHtml('');
    setPreviewSubject('');
  }, [template]);

  const refreshPreview = async () => {
    if (!template) return;
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}/preview`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Preview failed');
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Save failed');
      toast.success(`"${template.label}" updated`);
      onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    // Ask admin where to send (default = their own email)
    const defaultEmail = window.localStorage.getItem('admin_email') || '';
    const targetEmail = window.prompt(
      `Send a [TEST] preview to which email?\n\nThe email will use your current in-progress edits and your test values for placeholders.`,
      defaultEmail
    );
    if (!targetEmail || !targetEmail.includes('@')) return;
    if (defaultEmail !== targetEmail) {
      try { window.localStorage.setItem('admin_email', targetEmail); } catch { /* noop */ }
    }
    setTestSending(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}/test-send`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, to_email: targetEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Test send failed');
      toast.success(`Test email sent to ${targetEmail}. Check your inbox in a moment.`);
    } catch (e) {
      toast.error(e.message || 'Test send failed');
    } finally {
      setTestSending(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={!!template} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit: {template.label}
          </DialogTitle>
          <p className="text-xs text-gray-500">{template.description}</p>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 overflow-hidden">
          {/* Left: editor */}
          <div className="space-y-3 overflow-y-auto pr-2">
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Subject line…"
                className="mt-1"
                data-testid="tmpl-edit-subject"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase tracking-wide">Body (HTML)</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="HTML body…"
                className="mt-1 min-h-[340px] font-mono text-xs"
                data-testid="tmpl-edit-body"
              />
            </div>

            {/* Available variables */}
            <div className="border-t pt-3">
              <div className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">Available variables</div>
              <div className="text-xs text-gray-500 mb-2">
                Use these placeholders in your subject and body — they'll be filled in automatically at send time.
              </div>
              <div className="flex flex-wrap gap-2">
                {(template.variables || []).map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(`{{${v.key}}}`).then(() => toast.success(`{{${v.key}}} copied`))}
                    className="text-xs px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 border border-gray-200 font-mono"
                    title={`${v.label} — click to copy`}
                  >
                    {`{{${v.key}}}`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: live preview */}
          <div className="flex flex-col bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-white">
              <div className="text-xs font-medium text-gray-700 flex items-center gap-2">
                <Eye className="h-4 w-4" /> Live Preview
                {previewSubject && (
                  <span className="text-gray-500 truncate ml-2">— {previewSubject}</span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={refreshPreview} disabled={previewLoading} data-testid="tmpl-edit-refresh-preview">
                {previewLoading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <RotateCcw className="h-3.5 w-3.5 mr-1" />
                )}
                Refresh
              </Button>
            </div>
            <iframe
              title="email-preview"
              className="flex-1 w-full bg-white"
              style={{ minHeight: '420px' }}
              srcDoc={previewHtml || '<div style="font-family:sans-serif; padding:32px; color:#888;">Click Refresh to render the preview with your current edits…</div>'}
            />
          </div>
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            onClick={handleTestSend}
            disabled={testSending}
            data-testid="tmpl-edit-test-send"
            title="Send a one-off [TEST] preview to your inbox without saving"
          >
            {testSending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Test Send
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="tmpl-edit-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ----- Preview Dialog (default rendering) ---------------------------------

const TemplatePreviewDialog = ({ template, onClose }) => {
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!template) { setHtml(''); setSubject(''); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/email-templates/${template.id}/preview`, {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data?.detail || 'Preview failed');
        setHtml(data.html);
        setSubject(data.subject);
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [template]);

  if (!template) return null;

  return (
    <Dialog open={!!template} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg flex items-center gap-2">
            <Eye className="h-4 w-4" /> {template.label}
            {template.has_override && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs ml-2">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Showing default (your override hidden)
              </Badge>
            )}
          </DialogTitle>
          {subject && <p className="text-xs text-gray-500 mt-1">Subject: <strong>{subject}</strong></p>}
        </DialogHeader>
        {loading ? (
          <div className="p-10 text-center text-gray-400 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Rendering…
          </div>
        ) : (
          <iframe
            title="email-preview"
            className="w-full flex-1 bg-white border rounded-md"
            style={{ minHeight: '540px' }}
            srcDoc={html}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminEmailTemplates;
